import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { facilityService, TimeSlot } from 'services/facilityService';
import { reservationService, MyReservation } from 'services/reservationService';
import timeIcon from 'assets/ReserveImage/time.png';
import choose1Icon from 'assets/ReserveImage/choose1.png';
import choose2Icon from 'assets/ReserveImage/choose2.png';
import {
  Body,
  Header,
  BackButton,
  Content,
  FacilityImageSection,
  FacilityImage,
  FacilityImagePlaceholder,
  InfoCard,
  FacilityNameRow,
  FacilityName,
  LocationInfo,
  LocationIcon,
  Divider,
  DetailRow,
  DetailLabel,
  DetailValue,
  TimeDisplay,
  TimeDivider,
  IconText,
  LoadingContainer,
  ErrorMessage,
} from './ReservationDetail.styles';
import {
  TimeSection,
  SectionTitle,
  TimeTitleRow,
  LegendContainer,
  LegendItem,
  TimeSlots,
  TimeSlotButton,
  ConfirmButton,
} from './Reserve.styles';

const Extension: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<MyReservation | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingTimes, setIsLoadingTimes] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [reservationLimitMinutes, setReservationLimitMinutes] = useState<number | null>(null);

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchReservationDetail = async () => {
      if (!reservationId) {
        setError('예약 ID가 없습니다.');
        setIsLoading(false);
        return;
      }

      try {
        const reservationData = await reservationService.getReservationById(parseInt(reservationId));
        setReservation(reservationData);
      } catch (error) {
        console.error('Error fetching reservation detail:', error);
        setError('예약 정보를 불러오는데 실패했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservationDetail();
  }, [reservationId]);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!reservation) return;

      try {
        setIsLoadingTimes(true);
        const date = new Date(reservation.startTime);
        const dateString = date.toISOString().split('T')[0];
        const response = await facilityService.getFacilityTimes(reservation.facility.id, dateString);
        setTimeSlots(response.timeSlots);
        setReservationLimitMinutes(response.reservationLimitMinutes);
      } catch (error) {
        console.error('Failed to fetch time slots:', error);
      } finally {
        setIsLoadingTimes(false);
      }
    };

    fetchTimeSlots();
  }, [reservation]);

  const handleTimeSlotClick = (startTime: string) => {
    setSelectedTimeSlots(prev => {
      if (prev.includes(startTime)) {
        return prev.filter(time => time !== startTime);
      } else {
        // Check if adding this slot would exceed the reservation limit
        if (reservationLimitMinutes !== null) {
          const timeSlot = timeSlots.find(s => s.startTime === startTime);
          if (timeSlot) {
            const currentTotal = prev.reduce((sum, time) => {
              const slot = timeSlots.find(s => s.startTime === time);
              return sum + (slot?.durationMinutes || 0);
            }, 0);
            const newTotal = currentTotal + timeSlot.durationMinutes;

            if (newTotal > reservationLimitMinutes) {
              alert(`최대 ${reservationLimitMinutes}분까지만 연장할 수 있습니다.`);
              return prev;
            }
          }
        }
        return [...prev, startTime];
      }
    });
  };

  const calculateExtensionEndTime = (): string => {
    if (selectedTimeSlots.length === 0) return '';

    // 선택된 슬롯들을 시간순으로 정렬
    const sortedSlots = selectedTimeSlots
      .map(startTime => timeSlots.find(s => s.startTime === startTime))
      .filter((slot): slot is TimeSlot => slot !== undefined)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    if (sortedSlots.length === 0) return '';

    // 가장 마지막 슬롯의 종료 시간 계산
    const lastSlot = sortedSlots[sortedSlots.length - 1];
    const endTimeMs = new Date(lastSlot.startTime).getTime() + lastSlot.durationMinutes * 60 * 1000;
    const endDate = new Date(endTimeMs);

    // 로컬 시간을 유지하면서 ISO 형식으로 변환
    const year = endDate.getFullYear();
    const month = String(endDate.getMonth() + 1).padStart(2, '0');
    const day = String(endDate.getDate()).padStart(2, '0');
    const hours = String(endDate.getHours()).padStart(2, '0');
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    const seconds = String(endDate.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleConfirm = async () => {
    if (selectedTimeSlots.length === 0) {
      alert('연장할 시간을 선택해주세요.');
      return;
    }

    if (!reservationId) {
      alert('예약 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const endTime = calculateExtensionEndTime();
      if (!endTime) {
        alert('연장 시간을 계산할 수 없습니다.');
        return;
      }

      await reservationService.extendReservationById(Number(reservationId), { endTime });
      alert('예약이 연장되었습니다.');
      navigate(-1);
    } catch (error) {
      console.error('Failed to extend reservation:', error);
      alert('예약 연장에 실패했습니다.');
    }
  };

  const formatDateTime = (isoString: string): { date: string; time: string } => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const time = `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;

    return {
      date: `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`,
      time: time
    };
  };

  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    const hours = end.getHours();
    const minutes = end.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;
  };

  const getDayOfWeek = (isoString: string): string => {
    const date = new Date(isoString);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return days[date.getDay()];
  };

  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;
  };

  const isSlotInCurrentReservation = (slotStartTime: string): boolean => {
    if (!reservation) return false;

    const slotStart = new Date(slotStartTime);
    const reservationStart = new Date(reservation.startTime);
    const reservationEnd = new Date(reservationStart.getTime() + reservation.durationMinutes * 60 * 1000);

    return slotStart >= reservationStart && slotStart < reservationEnd;
  };

  const isSlotDisabled = (slot: TimeSlot): boolean => {
    if (slot.isBooked) return true;
    if (!reservation) return false;

    // 현재 예약 종료 시간 이전 슬롯은 비활성화 (이전 시간)
    // 단, 현재 이용중인 슬롯은 별도 처리하므로 제외
    const slotStart = new Date(slot.startTime);
    const reservationEnd = new Date(reservation.startTime).getTime() + reservation.durationMinutes * 60 * 1000;

    if (slotStart.getTime() < reservationEnd && !isSlotInCurrentReservation(slot.startTime)) {
      return true;
    }

    return false;
  };

  if (isLoading) {
    return (
      <Body>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          연장하기
        </Header>
        <LoadingContainer>로딩 중...</LoadingContainer>
      </Body>
    );
  }

  if (error || !reservation) {
    return (
      <Body>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          연장하기
        </Header>
        <ErrorMessage>{error || '예약을 찾을 수 없습니다.'}</ErrorMessage>
      </Body>
    );
  }

  const startDateTime = formatDateTime(reservation.startTime);
  const endTime = calculateEndTime(reservation.startTime, reservation.durationMinutes);
  const dayOfWeek = getDayOfWeek(reservation.startTime);
  const locationText = [
    reservation.facility.building?.name,
    reservation.facility.location
  ].filter(Boolean).join(' ');

  return (
    <Body>
      <Header>
        <BackButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </BackButton>
        연장하기
      </Header>

      <Content>
        {reservation.facility.imageUrl ? (
          <FacilityImageSection>
            <FacilityImage src={reservation.facility.imageUrl} alt={reservation.facility.name} />
          </FacilityImageSection>
        ) : (
          <FacilityImageSection>
            <FacilityImagePlaceholder>🏢</FacilityImagePlaceholder>
          </FacilityImageSection>
        )}

        <InfoCard>
          <FacilityNameRow>
            <FacilityName>{reservation.facility.name}</FacilityName>
          </FacilityNameRow>
          {locationText && (
            <LocationInfo>
              <LocationIcon>📍</LocationIcon>
              {locationText}
            </LocationInfo>
          )}

          <Divider />

          <DetailRow>
            <DetailLabel>예약 날짜</DetailLabel>
            <DetailValue>{startDateTime.date} ({dayOfWeek})</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailLabel>예약 시간</DetailLabel>
            <TimeDisplay>
              <DetailValue>{startDateTime.time}</DetailValue>
              <TimeDivider>~</TimeDivider>
              <DetailValue>{endTime}</DetailValue>
              <span style={{ color: '#999', fontSize: '14px', marginLeft: '8px' }}>
                ({reservation.durationMinutes}분)
              </span>
            </TimeDisplay>
          </DetailRow>

          <DetailRow>
            <DetailLabel>인원</DetailLabel>
            <DetailValue>
              <IconText>👥</IconText>
              {reservation.headCount}명
            </DetailValue>
          </DetailRow>
        </InfoCard>

        <InfoCard>
          <TimeSection style={{ padding: 0 }}>
            <TimeTitleRow>
              <SectionTitle>
                <img src={timeIcon} alt="시간" className="icon" style={{ width: '20px', height: '20px' }} />
                <div className="title">
                  연장 시간 선택
                  {reservationLimitMinutes !== null && (
                    <span style={{ color: '#999', fontSize: '14px', marginLeft: '8px' }}>
                      (최대 {reservationLimitMinutes}분)
                    </span>
                  )}
                </div>
              </SectionTitle>

              <LegendContainer>
                <LegendItem>
                  <img src={choose1Icon} alt="선택가능" />
                  <span>선택가능</span>
                </LegendItem>
                <LegendItem>
                  <img src={choose2Icon} alt="선택불가능" />
                  <span>선택불가능</span>
                </LegendItem>
              </LegendContainer>
            </TimeTitleRow>

            {isLoadingTimes ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                시간 조회 중...
              </div>
            ) : timeSlots.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                연장 가능한 시간이 없습니다.
              </div>
            ) : (
              <TimeSlots>
                {timeSlots.map((slot, index) => {
                  const disabled = isSlotDisabled(slot);
                  const isCurrentSlot = isSlotInCurrentReservation(slot.startTime);
                  return (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <TimeSlotButton
                        isDisabled={disabled}
                        isCurrentReservation={isCurrentSlot}
                        isSelected={selectedTimeSlots.includes(slot.startTime)}
                        onClick={() => !disabled && !isCurrentSlot && handleTimeSlotClick(slot.startTime)}
                        disabled={disabled || isCurrentSlot}
                      >
                        {formatTime(slot.startTime)}
                      </TimeSlotButton>
                      {isCurrentSlot && (
                        <div style={{ fontSize: '11px', color: '#1FBC70', textAlign: 'center', fontWeight: '500' }}>
                          현재 이용중
                        </div>
                      )}
                    </div>
                  );
                })}
              </TimeSlots>
            )}
          </TimeSection>
        </InfoCard>

        <ConfirmButton
          onClick={handleConfirm}
          disabled={selectedTimeSlots.length === 0}
          className={selectedTimeSlots.length > 0 ? 'active' : ''}
        >
          연장하기
        </ConfirmButton>
      </Content>
    </Body>
  );
};

export default Extension;
